import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

// Plain-English explanations for common marketing terms, reused across the site
export const HELP = {
  cpc: "Cost Per Click — what you pay each time someone clicks your ad. $5–$15 is normal in the trades.",
  ctr: "Click-Through Rate — of everyone who SEES your ad, how many click it. 4%+ on search ads is solid.",
  cpl: "Cost Per Lead — what you pay for each real phone call or form fill. Lower is better. This is the number to watch.",
  roi: "Return On Investment — money made vs. money spent. If $500 of ads books $3,000 of jobs, that's 6x. Above 3x is winning.",
  leads: "A lead is a real person who called or filled out your form — an actual potential customer, not just a click.",
  calls: "Phone calls tracked back to your campaigns. In the trades, calls book jobs — answer fast and you win.",
  spend: "What you've paid Google/Meta to run your ads. It never goes over the daily budget YOU set.",
  revenue: "Money from jobs this campaign booked — calculated with YOUR job prices, not our guesses.",
  bookedJobs: "Leads that turned into real scheduled work. The only number that pays the bills.",
  impressions: "How many times your ad appeared on someone's screen. Free — you only pay when they click.",
  conversions: "When a click turns into something you care about: a call or a booked form. This is what ads are FOR.",
};

export const HelpTooltip = ({ text, size = 14, testId = 'help-tooltip' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const tipRef = useRef(null);
  const hoverOpened = useRef(false);

  useLayoutEffect(() => {
    if (!open || !btnRef.current || !tipRef.current) return;
    const b = btnRef.current.getBoundingClientRect();
    const t = tipRef.current;
    const w = t.offsetWidth;
    const left = Math.max(8, Math.min(b.left + b.width / 2 - w / 2, window.innerWidth - w - 8));
    let top = b.bottom + 8;
    if (top + t.offsetHeight > window.innerHeight - 8) top = Math.max(8, b.top - t.offsetHeight - 8);
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || tipRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        aria-label="What does this mean?"
        aria-expanded={open}
        data-testid={testId}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (open && hoverOpened.current) { hoverOpened.current = false; return; }
          setOpen((o) => !o);
        }}
        onPointerEnter={(e) => { if (e.pointerType === 'mouse') { hoverOpened.current = true; setOpen(true); } }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') { hoverOpened.current = false; setOpen(false); } }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex shrink-0 align-middle text-gray hover:text-lime focus:text-lime focus:outline-none cursor-help transition-colors"
      >
        <HelpCircle size={size} />
      </button>
      {open && createPortal(
        <div
          ref={tipRef}
          role="tooltip"
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-[9999] w-72 max-w-[calc(100vw-16px)] rounded-xl border border-lime/30 bg-[#14304a] px-4 py-3 text-sm text-light-gray leading-relaxed shadow-2xl shadow-black/70 normal-case tracking-normal font-normal text-left"
          data-testid={`${testId}-popover`}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
};
